/**
 * 系統區共用快照（#518）：三頁共用的基礎數字只抓一次。
 *
 * 為什麼要有這一支：
 * - 系統區三段（總覽／連線中工作階段／AI 成效）各自掛載時各自打自己的資料。
 *   切一次頁，`OverviewPage` 會把 `/api/admin/overview` 重打一次，而那份回應
 *   （帳號數、連線數、未讀合計、今日註冊）在幾十秒內根本不會變。
 *   `OverviewPage` 一 unmount，`useEffect` 的重抓就什麼都不剩。
 * - 這裡把「三頁共用的那一份」放在模組層快取：第一次抓，之後（包含切回同一頁、
 *   或別頁需要同一份數字）直接吃快取，直到 TTL 到期或使用者手動刷新。
 *
 * 刻意**只快取 `/api/admin/overview`**：
 * - 各頁特有的資料（工作階段的原始列、執行紀錄、AI 趨勢）照舊各自抓，
 *   不進這個快取——它們的即時性要求不同，混在一起只會讓「重新整理」語意變模糊。
 * - 不引入 swr／react-query：這個 repo 沒有這兩個依賴，而需求只有「一份資料、
 *   一個 TTL、一個手動刷新」，用 40 行換掉一個新依賴不划算。
 *
 * 「單飛」（single-flight）：兩個消費者同時 mount 時只發一次請求，
 * 後到的直接等同一條 promise，不會出現兩份資料互相覆蓋。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
/** 基礎數字的有效期限：60 秒。比工作階段頁自己的 30 秒輪詢長，切頁時不會一直撞到過期。 */
export const SYSTEM_SNAPSHOT_TTL_MS = 60_000;
/** 以 client 物件本身當 key：換 client（重貼 token、重登入）自然就換一份快照。 */
const cache = new WeakMap();
/** 同一份 client 的進行中請求：單飛用。 */
const inflight = new WeakMap();
/** 回應形狀守門：舊 client／壞 client 回 `undefined` 要走空狀態，不是當資料用。 */
export function isOverviewShape(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate.accounts === 'object' &&
        candidate.accounts !== null &&
        typeof candidate.sessions === 'object' &&
        candidate.sessions !== null &&
        typeof candidate.registration === 'object' &&
        candidate.registration !== null &&
        Array.isArray(candidate.alerts));
}
function errMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
/** 讀快取；過期就當沒有（回 `null`），不主動清掉——舊資料留著才能在刷新失敗時繼續顯示。 */
function freshEntry(source) {
    const entry = cache.get(source);
    if (entry === undefined || entry.at === 0)
        return null;
    return Date.now() - entry.at < SYSTEM_SNAPSHOT_TTL_MS ? entry : null;
}
function toView(entry) {
    if (entry === undefined || entry === null) {
        return { data: null, phase: 'loading', error: null, fetchedAt: null };
    }
    return { data: entry.data, phase: entry.phase, error: entry.error, fetchedAt: entry.at === 0 ? null : entry.at };
}
async function fetchSnapshot(source, fn) {
    try {
        const result = await fn.call(source);
        if (!isOverviewShape(result)) {
            // 方法存在但回 undefined／形狀不對：當「還沒接線」，不是紅錯。
            return { data: null, phase: 'unavailable', error: null, at: 0 };
        }
        return { data: result, phase: 'ready', error: null, at: Date.now() };
    }
    catch (err) {
        // 失敗時保留上一份成功資料（若有），只把錯誤掛上去——切頁不該讓畫面變成空的。
        const previous = cache.get(source);
        return {
            data: previous?.data ?? null,
            phase: 'error',
            error: errMessage(err),
            at: previous?.at ?? 0,
        };
    }
}
/** 測試／登出用：把某個 client 的快照丟掉。 */
export function invalidateSystemSnapshot(source) {
    if (source !== undefined && source !== null)
        cache.delete(source);
}
/**
 * 取系統區共用快照。第一次呼叫會抓，之後 60 秒內（含切頁回來）直接用快取。
 * `refresh()` 無視 TTL 重抓；呼叫端自己決定按鈕何時叫它。
 */
export function useSystemSnapshot(client) {
    const [view, setView] = useState(() => toView(client ? cache.get(client) : null));
    /** 卸載後不要寫 state（切分頁會 unmount 這一頁）；但請求本身照樣跑完並寫進快取。 */
    const alive = useRef(true);
    const request = useCallback((force) => {
        const source = (client ?? null);
        const fn = client?.overview;
        if (source === null || typeof fn !== 'function') {
            setView({ data: null, phase: 'unavailable', error: null, fetchedAt: null });
            return;
        }
        if (!force) {
            const cached = freshEntry(source);
            if (cached !== null) {
                setView(toView(cached));
                return;
            }
        }
        setView((prev) => ({ ...prev, phase: 'loading', error: null }));
        const running = inflight.get(source);
        if (running !== undefined) {
            void running.then((entry) => {
                cache.set(source, entry);
                if (alive.current)
                    setView(toView(entry));
            });
            return;
        }
        const promise = fetchSnapshot(source, fn).then((entry) => {
            cache.set(source, entry);
            inflight.delete(source);
            return entry;
        });
        inflight.set(source, promise);
        void promise.then((entry) => {
            if (!alive.current)
                return;
            setView(toView(entry));
        });
    }, [client]);
    useEffect(() => {
        alive.current = true;
        request(false);
        return () => {
            alive.current = false;
        };
    }, [request]);
    const refresh = useCallback(() => request(true), [request]);
    return { ...view, refresh };
}
//# sourceMappingURL=useSystemSnapshot.js.map